/**
 * Role metadata for the help centre.
 *
 * Each role has a curated, ordered list of article slugs grouped into
 * sections. The same article slug can appear under multiple roles —
 * the article body itself is role-agnostic and notes any permission
 * differences inline. See `articles.js` for slug → component lookup.
 */

export const ROLES = {
  school_admins: {
    slug: "school_admins",
    label: "School admins",
    short: "Admins",
    description:
      "You set up the read-a-thon, add students, connect the bank account, and watch the whole school's progress.",
    helpPath: "/help/school-admins",
    sections: [
      {
        name: "Get set up",
        slugs: [
          "getting-started-school-admin",
          "creating-a-campaign",
          "paying-for-your-campaign",
          "connecting-square",
          "importing-roster-csv",
          "inviting-teachers",
          "printing-take-home-sheets",
          "setting-school-milestones",
        ],
      },
      {
        name: "Rewards & prizes",
        slugs: [
          "setting-student-prizes",
          "setting-classroom-goals-and-milestones",
          "handing-out-student-prizes",
        ],
      },
      {
        name: "More setup",
        slugs: [
          "campaign-lifecycle",
          "adding-classrooms-manually",
          "rotating-activation-codes",
          "managing-members",
          "school-settings",
        ],
      },
      {
        name: "Run the read-a-thon",
        slugs: [
          "school-admin-dashboard",
          "logging-classroom-minutes",
          "logging-solo-minutes",
          "undoing-minute-batches",
          "tracking-donations",
        ],
      },
      {
        name: "Wrap up",
        slugs: [
          "after-the-campaign",
          "refunds-via-square",
          "fees-and-pricing",
        ],
      },
    ],
  },

  teachers: {
    slug: "teachers",
    label: "Teachers",
    short: "Teachers",
    description:
      "You log the class's reading time, watch your room climb the leaderboard, and hand out student prizes.",
    helpPath: "/help/teachers",
    sections: [
      {
        name: "Get started",
        slugs: [
          "getting-started-teacher",
          "teacher-classroom-dashboard",
        ],
      },
      {
        name: "Day-to-day",
        slugs: [
          "logging-classroom-minutes",
          "logging-solo-minutes",
          "undoing-minute-batches",
        ],
      },
      {
        name: "Goals, rewards & prizes",
        slugs: [
          "setting-classroom-goals-and-milestones",
          "handing-out-student-prizes",
        ],
      },
      {
        name: "Take-home sheets",
        slugs: [
          "printing-take-home-sheets",
          "rotating-activation-codes",
        ],
      },
    ],
  },

  parents: {
    slug: "parents",
    label: "Parents & families",
    short: "Parents",
    description:
      "You sign your child up, follow their reading, log minutes from home, and share the donation page with family.",
    helpPath: "/help/parents",
    sections: [
      {
        name: "Get started",
        slugs: [
          "activating-your-child",
          "parent-magic-link-signin",
          "parent-dashboard",
          "multiple-children",
        ],
      },
      {
        name: "Read & share",
        slugs: [
          "logging-minutes-at-home",
          "setting-personal-goals",
          "sharing-with-family",
          "leaderboards-and-privacy",
        ],
      },
    ],
  },

  donors: {
    slug: "donors",
    label: "Donors",
    short: "Donors",
    description:
      "You're chipping in for a student you know. No account needed — just a card and 30 seconds.",
    helpPath: "/help/donors",
    sections: [
      {
        name: "Donate",
        slugs: [
          "making-a-donation",
          "donating-without-an-account",
          "covering-processing-fees",
          "anonymous-donations-and-messages",
        ],
      },
      {
        name: "After donating",
        slugs: [
          "donor-receipts-and-refunds",
        ],
      },
    ],
  },
};

export const ROLE_ORDER = ["school_admins", "teachers", "parents", "donors"];

export function roleFor(slug) {
  return ROLES[slug] || null;
}

/**
 * Given an article slug, infer the most natural "from" role to use
 * for side-nav context when the URL didn't specify one. Picks the
 * first role in ROLE_ORDER that lists this article.
 */
export function defaultRoleForArticle(articleSlug, articleRoles = []) {
  for (const r of ROLE_ORDER) {
    if (articleRoles.includes(r)) return r;
  }
  return null;
}
